import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import CrudListPage from "../../../components/crud/CrudListPage";
import DataTable, { formatCurrency } from "../../../components/crud/DataTable";
import DetailPanel, { PanelSection, PanelRow, SummaryCard, SummaryRow } from "../../../components/v4/DetailPanel";
import { FiBarChart2, FiEdit2 } from "react-icons/fi";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

// Chart colors matching the app theme
const CHART_COLORS = {
	positive: "#2ecc71",
	negative: "#e74c3c",
};

// Format compact currency for Y-axis labels
const formatCompact = (value) => {
	const absValue = Math.abs(value);
	const sign = value < 0 ? "-" : "";
	if (absValue >= 1000000) {
		return `${sign}$${(absValue / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
	} else if (absValue >= 1000) {
		return `${sign}$${(absValue / 1000).toFixed(1).replace(/\.0$/, "")}k`;
	}
	return `${sign}$${absValue}`;
};

const NetWorthList = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [hoverIndex, setHoverIndex] = useState(null);
	const chartAreaRef = useRef(null);
	const [selectedItem, setSelectedItem] = useState(null);

	useEffect(() => {
		const fetchEntries = async () => {
			try {
				const res = await axios.get(`${backendUrl}/networth/entries`);
				setData(res.data);
			} catch (err) {
				console.error("Error fetching net worth entries:", err);
			} finally {
				setLoading(false);
			}
		};
		fetchEntries();
	}, []);

	const handleDelete = async (row) => {
		if (!window.confirm(`Delete entry from ${new Date(row.date).toLocaleDateString()}?`)) return;

		try {
			await axios.delete(`${backendUrl}/networth/entries/${row.id}`);
			setData((prev) => prev.filter((item) => item.id !== row.id));
			if (selectedItem?.id === row.id) {
				setSelectedItem(null);
			}
		} catch (err) {
			console.error("Error deleting net worth entry:", err);
		}
	};

	const handleRowClick = (row) => {
		setSelectedItem(row);
	};

	// Render panel content
	const renderPanelContent = () => {
		if (!selectedItem) return null;

		const numValue = parseFloat(selectedItem.value);

		return (
			<>
				<PanelSection title="Entry Details">
					<PanelRow
						label="Date"
						value={new Date(selectedItem.date).toLocaleDateString("en-US", {
							month: "long",
							day: "numeric",
							year: "numeric",
						})}
					/>
					<PanelRow
						label="Net Worth"
						value={formatCurrency(numValue)}
					/>
				</PanelSection>

				{selectedItem.note && (
					<PanelSection title="Note">
						<p style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "14px", color: "var(--v4-text-secondary)" }}>
							{selectedItem.note}
						</p>
					</PanelSection>
				)}
			</>
		);
	};

	// Render panel sidebar
	const renderPanelSidebar = () => {
		if (!selectedItem) return null;

		const numValue = parseFloat(selectedItem.value);

		return (
			<SummaryCard label="Net Worth">
				<SummaryRow
					label="Value"
					value={formatCurrency(numValue)}
				/>
			</SummaryCard>
		);
	};

	// Panel footer
	const renderPanelFooter = () => {
		if (!selectedItem) return null;
		return (
			<Link to={`/networth/${selectedItem.id}`} className="l-btn">
				<FiEdit2 style={{ marginRight: 6 }} />
				Edit
			</Link>
		);
	};

	// Sort data by date ascending for chart (oldest first)
	const chartData = useMemo(() => {
		return [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
	}, [data]);

	// Calculate chart dimensions
	const chartStats = useMemo(() => {
		if (chartData.length === 0) return null;

		const values = chartData.map((d) => parseFloat(d.value));
		const maxValue = Math.max(...values);
		const minValue = Math.min(...values, 0);
		const range = maxValue - minValue || 1;

		return { maxValue, minValue, range };
	}, [chartData]);

	// Handle mouse move on chart to find nearest data point
	const handleChartMouseMove = useCallback(
		(e) => {
			if (!chartAreaRef.current || chartData.length < 2) return;

			const rect = chartAreaRef.current.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const percentage = x / rect.width;

			// Find nearest data point index
			const exactIndex = percentage * (chartData.length - 1);
			const nearestIndex = Math.round(exactIndex);
			const clampedIndex = Math.max(0, Math.min(chartData.length - 1, nearestIndex));

			setHoverIndex(clampedIndex);
		},
		[chartData.length]
	);

	const handleChartMouseLeave = useCallback(() => {
		setHoverIndex(null);
	}, []);

	const columns = [
		{
			key: "date",
			header: "Date",
			width: "sm",
			render: (date, row) => (
				<Link to={`/networth/${row.id}`} style={{ fontWeight: 600 }}>
					{new Date(date).toLocaleDateString("en-US", {
						month: "short",
						day: "numeric",
						year: "numeric",
					})}
				</Link>
			),
		},
		{
			key: "value",
			header: "Net Worth",
			width: "sm",
			render: (value) => {
				const numValue = parseFloat(value);
				return (
					<span style={{ color: numValue >= 0 ? CHART_COLORS.positive : CHART_COLORS.negative, fontWeight: 600 }}>
						{formatCurrency(numValue)}
					</span>
				);
			},
		},
		{
			key: "note",
			header: "Note",
			render: (note) => (note ? (note.length > 50 ? note.substring(0, 50) + "..." : note) : "—"),
		},
	];

	// Calculate zero line position
	const zeroY = chartStats && chartStats.minValue < 0 ? 100 - ((0 - chartStats.minValue) / chartStats.range) * 100 : null;

	return (
		<>
			<CrudListPage
				title="Net Worth History"
				addLink="/networth/new"
				addLabel="Add Entry"
				variant="v4"
				icon={<FiBarChart2 />}
			>
				{/* Line Chart */}
				{chartData.length > 1 && chartStats && (
				<div className="networth-chart">
					<div className="networth-chart__title">Net Worth Over Time</div>
					<div
						className="networth-chart__area"
						ref={chartAreaRef}
						onMouseMove={handleChartMouseMove}
						onMouseLeave={handleChartMouseLeave}
					>
						<div className="networth-chart__yaxis">
							<span style={{ top: 0 }}>{formatCompact(chartStats.maxValue)}</span>
							{zeroY !== null && <span style={{ top: `${zeroY}%` }}>$0</span>}
							<span style={{ top: "100%" }}>{formatCompact(chartStats.minValue)}</span>
						</div>
						<svg viewBox="0 0 1000 100" preserveAspectRatio="none" className="networth-chart__svg">
							{/* Month/Year vertical grid lines */}
							{chartData.map((d, i) => {
								if (i === 0) return null;
								const x = (i / (chartData.length - 1)) * 1000;
								const date = new Date(d.date);
								const isJanuary = date.getMonth() === 0;
								return (
									<line
										key={`grid-${i}`}
										x1={x}
										y1="0"
										x2={x}
										y2="100"
										stroke={isJanuary ? "#ccc" : "#eee"}
										strokeWidth={isJanuary ? "1" : "0.5"}
										vectorEffect="non-scaling-stroke"
									/>
								);
							})}
							{/* Zero line if applicable */}
							{chartStats.minValue < 0 && (
								<line
									x1="0"
									y1={100 - ((0 - chartStats.minValue) / chartStats.range) * 100}
									x2="1000"
									y2={100 - ((0 - chartStats.minValue) / chartStats.range) * 100}
									stroke="#e0e0e0"
									strokeDasharray="4"
								/>
							)}
							{/* Line segments */}
							{chartData.map((d, i) => {
								if (i === 0) return null;
								const prev = chartData[i - 1];
								const x1 = ((i - 1) / (chartData.length - 1)) * 1000;
								const y1 = 100 - ((parseFloat(prev.value) - chartStats.minValue) / chartStats.range) * 100;
								const x2 = (i / (chartData.length - 1)) * 1000;
								const y2 = 100 - ((parseFloat(d.value) - chartStats.minValue) / chartStats.range) * 100;
								const isNegative = parseFloat(prev.value) < 0 || parseFloat(d.value) < 0;
								return (
									<line
										key={`line-${i}`}
										x1={x1}
										y1={y1}
										x2={x2}
										y2={y2}
										stroke={isNegative ? CHART_COLORS.negative : CHART_COLORS.positive}
										strokeWidth="2"
										vectorEffect="non-scaling-stroke"
									/>
								);
							})}
							{/* Hover vertical line */}
							{hoverIndex !== null && (
								<line
									x1={(hoverIndex / (chartData.length - 1)) * 1000}
									y1="0"
									x2={(hoverIndex / (chartData.length - 1)) * 1000}
									y2="100"
									stroke="#666"
									strokeWidth="1"
									strokeDasharray="4"
									vectorEffect="non-scaling-stroke"
								/>
							)}
						</svg>
						<div className="networth-chart__points">
							{chartData.map((d, i) => {
								const value = parseFloat(d.value);
								const isHovered = hoverIndex === i;
								return (
									<div
										key={i}
										className={`networth-chart__point ${value < 0 ? "networth-chart__point--negative" : ""} ${isHovered ? "networth-chart__point--hovered" : ""}`}
										style={{
											left: `${(i / (chartData.length - 1)) * 100}%`,
											top: `${100 - ((value - chartStats.minValue) / chartStats.range) * 100}%`,
										}}
									/>
								);
							})}
						</div>
						{/* Hover tooltip */}
						{hoverIndex !== null && chartData[hoverIndex] && (
							<div
								className="networth-chart__tooltip"
								style={{
									left: `${(hoverIndex / (chartData.length - 1)) * 100}%`,
									top: `${100 - ((parseFloat(chartData[hoverIndex].value) - chartStats.minValue) / chartStats.range) * 100}%`,
								}}
							>
								<div className="networth-chart__tooltip-date">
									{new Date(chartData[hoverIndex].date).toLocaleDateString("en-US", {
										month: "short",
										day: "numeric",
										year: "numeric",
									})}
								</div>
								<div
									className="networth-chart__tooltip-value"
									style={{ color: parseFloat(chartData[hoverIndex].value) >= 0 ? CHART_COLORS.positive : CHART_COLORS.negative }}
								>
									{formatCurrency(parseFloat(chartData[hoverIndex].value))}
								</div>
							</div>
						)}
					</div>
					<div className="networth-chart__labels">
						{chartData
							.filter((_, i) => chartData.length <= 12 || i % Math.ceil(chartData.length / 12) === 0)
							.map((d, i) => (
								<span key={i}>
									{new Date(d.date).toLocaleDateString("en-US", { month: "short", year: "2-digit" })}
								</span>
							))}
					</div>
				</div>
			)}

			{chartData.length === 1 && (
				<div className="networth-chart networth-chart--empty">
					<p>Add more entries to see the trend chart.</p>
				</div>
			)}

				{/* Data Table */}
				<DataTable
					columns={columns}
					data={data}
					onDelete={handleDelete}
					loading={loading}
					emptyMessage="No net worth entries yet. Add your first entry above."
					variant="v4"
					onRowClick={handleRowClick}
					selectedId={selectedItem?.id}
				/>

				<style>{`
				.networth-chart {
					margin-bottom: 2rem;
					padding: 1.5rem;
					background: var(--v4-surface);
					border: 1px solid var(--v4-border);
					border-radius: var(--v4-radius);
				}

				.networth-chart--empty {
					text-align: center;
					color: var(--v4-text-muted);
				}

				.networth-chart__title {
					font-size: 1.1rem;
					font-weight: 600;
					margin-bottom: 1rem;
					color: var(--v4-text);
				}

				.networth-chart__area {
					position: relative;
					height: 200px;
					margin-left: 50px;
				}

				.networth-chart__yaxis {
					position: absolute;
					left: -50px;
					top: 0;
					bottom: 0;
					width: 45px;
					display: flex;
					flex-direction: column;
					justify-content: space-between;
					font-size: 0.75rem;
					color: var(--v4-text-muted);
				}

				.networth-chart__yaxis span {
					position: absolute;
					right: 5px;
					transform: translateY(-50%);
				}

				.networth-chart__svg {
					width: 100%;
					height: 100%;
				}

				.networth-chart__points {
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					pointer-events: none;
				}

				.networth-chart__point {
					position: absolute;
					width: 8px;
					height: 8px;
					border-radius: 50%;
					background: ${CHART_COLORS.positive};
					transform: translate(-50%, -50%);
					pointer-events: auto;
					cursor: pointer;
				}

				.networth-chart__point--negative {
					background: ${CHART_COLORS.negative};
				}

				.networth-chart__point--hovered {
					width: 12px;
					height: 12px;
					z-index: 10;
					box-shadow: 0 0 0 3px rgba(46, 204, 113, 0.3);
				}

				.networth-chart__point--hovered.networth-chart__point--negative {
					box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.3);
				}

				.networth-chart__tooltip {
					position: absolute;
					transform: translate(-50%, -100%);
					margin-top: -15px;
					background: rgba(0, 0, 0, 0.85);
					color: white;
					padding: 8px 12px;
					border-radius: 6px;
					font-size: 0.85rem;
					white-space: nowrap;
					pointer-events: none;
					z-index: 20;
				}

				.networth-chart__tooltip::after {
					content: '';
					position: absolute;
					top: 100%;
					left: 50%;
					transform: translateX(-50%);
					border: 6px solid transparent;
					border-top-color: rgba(0, 0, 0, 0.85);
				}

				.networth-chart__tooltip-date {
					font-size: 0.75rem;
					opacity: 0.8;
					margin-bottom: 2px;
				}

				.networth-chart__tooltip-value {
					font-weight: 600;
					font-size: 1rem;
				}

				.networth-chart__labels {
					display: flex;
					justify-content: space-between;
					margin-top: 0.5rem;
					margin-left: 50px;
					font-size: 0.75rem;
					color: var(--v4-text-muted);
				}
				`}</style>
			</CrudListPage>

			<DetailPanel
				isOpen={!!selectedItem}
				onClose={() => setSelectedItem(null)}
				title={selectedItem ? new Date(selectedItem.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
				subtitle="Net Worth Entry"
				sidebar={renderPanelSidebar()}
				footer={renderPanelFooter()}
			>
				{renderPanelContent()}
			</DetailPanel>
		</>
	);
};

export default NetWorthList;
